import React, { useState } from "react";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";

// Relative ranges rather than a date picker.
//
// react-datepicker is physically present under node_modules — @repoflow/form-
// schema has a `date` widget that uses it — but only as an undeclared optional
// peer that this app never asks for. Registering that widget would mean adding
// the dependency and re-resolving the lockfile, which is the trap npm/README.md
// documents, to replace a control every supported browser already ships as
// <input type="datetime-local">. And a relative range is what actually gets
// clicked: "the last hour" is the question, not a pair of calendar dates.
const RANGES = [
  { key: "1h", label: "Last hour", ms: 60 * 60 * 1000 },
  { key: "24h", label: "Last 24 hours", ms: 24 * 60 * 60 * 1000 },
  { key: "7d", label: "Last 7 days", ms: 7 * 24 * 60 * 60 * 1000 },
  { key: "30d", label: "Last 30 days", ms: 30 * 24 * 60 * 60 * 1000 },
  { key: "custom", label: "Custom..." },
];

// The server caps a window at 31 days, so there is no range here that it would
// silently clamp. Keep that true if a longer option is ever added.
export const DEFAULT_RANGE = "24h";

// Names the window in a card header and under every KPI tile, so a total is never
// shown without saying what it is a total OF. The same function the organization
// dashboard's filter exports, for the same reason.
export const rangeLabel = (range) =>
  (RANGES.find((r) => r.key === range) || {}).label || "Selected range";

export const buildFilter = ({ range, start, end, eventType, dimensionValues }) => {
  const filter = {};

  if (range === "custom") {
    // Both are datetime-local values — no zone — so they are read as local time
    // and sent as ISO, which is what the server parses.
    if (start) filter.start = new Date(start).toISOString();
    if (end) filter.end = new Date(end).toISOString();
  } else {
    const { ms } = RANGES.find((r) => r.key === range) || {};
    if (ms) filter.start = new Date(Date.now() - ms).toISOString();
  }

  if (eventType) filter.eventType = eventType;

  const dimensions = Object.entries(dimensionValues || {})
    .filter(([, value]) => value !== "")
    .map(([name, value]) => ({ name, value }));
  if (dimensions.length) filter.dimensions = dimensions;

  return filter;
};

// Submit-only, like the bucket search box: every change here re-runs a billed
// Timestream query, so filtering on keystroke would be expensive as well as
// noisy — and since 2026-08-13 an apply re-runs up to three of them, because the
// charts below read the aggregate over the same slice.
//
// `onApply` receives the RANGE alongside the filter. The filter is what the
// server is asked for; the range is what the client needs on its own — the bin
// width to request (intervalForRange) and the words under each tile — and it
// cannot be recovered from the filter, where "last 24 hours" has already become
// an absolute start timestamp.
export const Filter = ({ dimensions = [], onApply }) => {
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [eventType, setEventType] = useState("");
  const [dimensionValues, setDimensionValues] = useState({});

  const submit = (event) => {
    event.preventDefault();
    onApply({
      filter: buildFilter({ range, start, end, eventType, dimensionValues }),
      range,
    });
  };

  return (
    <Form onSubmit={submit} className="mb-3">
      <Row form>
        <Col md={3}>
          <FormGroup>
            <Label for="capture-range">Range</Label>
            <Input
              id="capture-range"
              type="select"
              value={range}
              onChange={(e) => setRange(e.target.value)}
            >
              {RANGES.map(({ key, label }) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>

        {range === "custom" && (
          <>
            <Col md={3}>
              <FormGroup>
                <Label for="capture-start">From</Label>
                <Input
                  id="capture-start"
                  type="datetime-local"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="capture-end">To</Label>
                <Input
                  id="capture-end"
                  type="datetime-local"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                />
              </FormGroup>
            </Col>
          </>
        )}

        <Col md={3}>
          <FormGroup>
            <Label for="capture-event-type">Event Type</Label>
            <Input
              id="capture-event-type"
              type="text"
              placeholder="any"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
            />
          </FormGroup>
        </Col>
      </Row>

      {/* One input per declared dimension rather than an add/remove filter
          builder: a point declares a handful of dimensions and they are fixed,
          so showing them all is both simpler and a better answer to "what can I
          filter on here?". */}
      {dimensions.length > 0 && (
        <Row form>
          {dimensions.map(({ name }) => (
            <Col md={3} key={name}>
              <FormGroup>
                <Label for={`capture-dimension-${name}`}>{name}</Label>
                <Input
                  id={`capture-dimension-${name}`}
                  type="text"
                  placeholder="any"
                  value={dimensionValues[name] || ""}
                  onChange={(e) =>
                    setDimensionValues((current) => ({
                      ...current,
                      [name]: e.target.value,
                    }))
                  }
                />
              </FormGroup>
            </Col>
          ))}
        </Row>
      )}

      <Button color="primary" type="submit">
        <i className="fa fa-search" /> Apply
      </Button>
    </Form>
  );
};
